import { useQuery } from '@tanstack/react-query'
import { QueryKeys } from 'constants/query-keys'
import {
    fetchTheftExternal,
    fetchTheftInternal,
} from 'queries/assets-protection/theft/theft'
import { AssetsProtectionTheftProps } from 'types/api'
import { IResponse } from 'utils/api'
import { getGreenfieldDrillValues } from 'utils/greenfield'
import {
    isCompany,
    isDistrict,
    isGroup,
    isRegion,
    isStore,
} from 'utils/location'
import { TIME } from 'utils/misc'
import { useAnalytics } from 'utils/analytics'

// RGD External theft expects a title-case filter_dimension; unlike other Greenfield cards
export const getTheftFilterDimension = (
    location: string,
): string | undefined => {
    if (isCompany(location)) return undefined
    if (isRegion(location)) return 'Region'
    if (isGroup(location)) return 'Group'
    if (isDistrict(location)) return 'District'
    return undefined
}

export const getTheftRGDInternalFilters = (
    location: string,
): Record<string, string> => {
    if (isCompany(location)) return { prepend_footer_to_rows: 'true' }
    const drillValues = getGreenfieldDrillValues(location) ?? {}
    return {
        ...drillValues,
        prepend_footer_to_rows: 'true',
    }
}

export const getTheftRGDExternalFilters = (
    location: string,
): Record<string, string> => {
    const dimension = getTheftFilterDimension(location)
    if (!dimension) return { prepend_footer_to_rows: 'true' }
    return {
        drill_dimension: dimension,
        drill_value: location,
        prepend_footer_to_rows: 'true',
    }
}

export function useGetTheftRGDInternal({
    env,
    accessToken,
    location,
}: AssetsProtectionTheftProps): IResponse {
    const { tenMinutes } = TIME
    const enabled = !isStore(location)
    const { trackEvent } = useAnalytics()
    const filters = getTheftRGDInternalFilters(location)

    const { data, isLoading, isError, refetch, remove, error } = useQuery(
        [QueryKeys.THEFT_RGD_INTERNAL, location, filters],
        () =>
            fetchTheftInternal({
                env,
                accessToken,
                filters,
                method: 'GET',
                trackEvent,
            }),
        {
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            enabled,
            retry: false,
        },
    )

    return { data, isLoading, isError, refetch, remove, error }
}
export function useGetTheftRGDExternal({
    env,
    accessToken,
    location,
}: AssetsProtectionTheftProps): IResponse {
    const { tenMinutes } = TIME
    const enabled = !isStore(location)
    const { trackEvent } = useAnalytics()
    const filters = getTheftRGDExternalFilters(location)

    const { data, isLoading, isError, refetch, remove, error } = useQuery(
        [QueryKeys.THEFT_RGD_EXTERNAL, location, filters],
        () =>
            fetchTheftExternal({
                env,
                accessToken,
                filters,
                method: 'GET',
                trackEvent,
            }),
        {
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            enabled,
            retry: false,
        },
    )

    return { data, isLoading, isError, refetch, remove, error }
}
